import { CloseOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import {
  useSubscription,
  useMutation,
  useQuery,
  useLazyQuery,
} from "@apollo/react-hooks";
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Row,
  TimePicker,
  Tooltip,
  Drawer,
  InputNumber,
  Divider,
  Space,
  Modal,
} from "antd";
import axios from "axios";
import moment from "moment";

import React, { useEffect, useState } from "react";
import { Loader } from "../../Loader";
import "./AddLocationDrawer.less";
import { GET_USER_SUBSCRIPTION } from "../../../Graphql/subscriptions";
import {
  INSERT_LOCATION,
  UPSERT_EMPLOYEE_COUNT,
  UPDATE_BUSINESS,
  UPDATE_SUBSCRIPTION_ON_LOCATION,
  UPDATE_SUBSCRIPTION,
} from "../../../Graphql/mutations";
import {
  GET_LOCATION_DETAILS_BY_OWNER,
  GET_CARD_DETAILS,
  GET_UPCOMING_INVOICE,
} from "../../../Graphql/queries";

export const AddLocationDrawer = ({ visible, toggleDrawer }) => {
  const [form] = Form.useForm();
  const [subscriptionType, setSubscriptionType] = useState("month");
  const [upcomingStripePriceId, setUpcomingStripePriceId] = useState("");
  const [pay, setPay] = useState(0);
  const { data: subscriptionData, loading: subscriptionDataLoading } =
    useSubscription(GET_USER_SUBSCRIPTION, {
      variables: {
        user_id: localStorage.getItem("user_id"),
      },
    });
  const [UPDATE_SUBSCRIPTION_MUTATION, { loading: isUpdatingSubscription }] =
    useMutation(UPDATE_SUBSCRIPTION_ON_LOCATION, {
      context: {
        headers: {
          subscription: 1,
        },
      },
    });
  const [GET_UPCOMING_INVOICE_DATA, { data: upcomingInvoiceData }] =
    useLazyQuery(GET_UPCOMING_INVOICE, {
      fetchPolicy: "cache-and-network",
    });
  const [
    GET_CARD,
    { data: cardDetails, loading: cardDetailsLoading, refetch },
  ] = useLazyQuery(GET_CARD_DETAILS);
  const [UPGRAD_SUBSCRIPTION_MUTATION, { loading: isUpgradingSubscription }] =
    useMutation(UPDATE_SUBSCRIPTION);
  useEffect(() => {
    GET_UPCOMING_INVOICE_DATA();
    GET_CARD();
  }, []);

  useEffect(() => {
    if (!!upcomingInvoiceData && !!subscriptionData) {
      if (
        upcomingInvoiceData?.get_upcoming_invoice?.upcomingInvoice?.lines
          ?.data[0]?.plan.id !==
        subscriptionData?.user_subscription[0]?.stripe_price_id
      ) {
        setUpcomingStripePriceId(
          upcomingInvoiceData?.get_upcoming_invoice?.upcomingInvoice?.lines
            ?.data[0]?.plan.id
        );
      }
    }
  }, [upcomingInvoiceData, subscriptionData]);
  function confirm(values) {
    Modal.confirm({
      title: "Confirm",
      onOk() {
        onAddLocation(values);
      },
      icon: <ExclamationCircleOutlined />,
      content: (
        <div>
          <span>
            {`Adding locations is chargeable. Your credit card xxxx xxxx xxxx ${cardDetails?.get_payment_methods?.cards[0]?.card?.last4}`}{" "}
          </span>
          <span>
            {` ${
              subscriptionData?.user_subscription[0]?.subscriptionStatus
                ?.status === "trial"
                ? "will be billed from the next billing cycle."
                : "will be billed now."
            }`}
            <br></br> <br></br>Do you want to proceed?
          </span>
        </div>
      ),
      okText: "YES",
      cancelText: "NO",
    });
  }
  const { data: locationDetails } = useQuery(GET_LOCATION_DETAILS_BY_OWNER, {
    variables: {
      user_id: localStorage.getItem("user_id"),
    },
    fetchPolicy: "cache-and-network",
    context: {
      headers: {
        subscription: 1,
      },
    },
  });
  useEffect(() => {
    const daysRemaining =
      new Date(
        new Date().getFullYear(),
        new Date().getMonth() + 1,
        0
      ).getDate() - new Date().getDate();
    const totalDays = new Date(
      new Date().getFullYear(),
      new Date().getMonth() + 1,
      0
    ).getDate();
    const type = subscriptionData?.user_subscription[0]?.subscriptionType?.type;
    const payableValue =
      (daysRemaining *
        (subscriptionData?.user_subscription[0]?.subscription_transactions[0]
          ?.amount_paid || 99)) /
      totalDays;
    setPay(payableValue.toFixed(2));
    setSubscriptionType(type);
  }, [subscriptionData]);

  const [INSERT_LOCATIONS_MUTATION, { loading: insertLocationLoading }] =
    useMutation(INSERT_LOCATION, {
      context: {
        headers: {
          subscription: 1,
        },
      },
    });

  const [UPSERT_EMPLOYEE_COUNT_MUTATION, { loading: upsertEmployeeLoading }] =
    useMutation(UPSERT_EMPLOYEE_COUNT);

  const [UPDATE_BUSINESS_MUTATION, { loading: updateBussinessLoading }] =
    useMutation(UPDATE_BUSINESS, {
      context: {
        headers: {
          subscription: 1,
        },
      },
    });
  const updateSubscription = async () => {
    const isUpdateSubscriptionSuccessful = await UPDATE_SUBSCRIPTION_MUTATION({
      variables: {
        price_id: subscriptionData?.user_subscription[0]?.stripe_price_id,
        locations: subscriptionData?.user_subscription[0]?.locations + 1,
      },
    });
    return isUpdateSubscriptionSuccessful;
  };

  const onAddLocation = async (values) => {
    const subcriptionResult = await updateSubscription();
    if (subcriptionResult.errors) {
      message.error("Error occured while adding new location");
      toggleDrawer();
      return;
    }
    let insertLocations = [];
    let locationTimings = [];
    if (
      !values[`start_time`]?.format("HH:mm") ||
      !values[`end_time`]?.format("HH:mm")
    ) {
      locationTimings = [];
    } else {
      [...Array.from(Array(7).keys())].forEach((i) => {
        locationTimings.push({
          working_day: moment().set("day", i).format("dddd"),
          opening_time: values[`start_time`]?.format("HH:mm") || "",
          closing_time: values[`end_time`]?.format("HH:mm") || "",
        });
      });
    }
    insertLocations.push({
      user_id: localStorage.getItem("user_id"),
      business_id: locationDetails.businesses[0].business_id,
      job_role_id: locationDetails.roleDetails[0].role_id,
      locationDetails: {
        data: {
          location_name: values[`location_name`],
          street_address: values[`street_address`],
          zipcode: values[`zipcode`],
          city: values[`city`],
          state: values[`state`],
          country: values[`country`],
          total_employees: parseInt(values[`count`]),
          timezone_id: form.getFieldValue(`timezone_id`),
          timezone_name: form.getFieldValue(`timezone_name`),
          location_timings: {
            data: locationTimings,
          },
        },
      },
    });
    if (insertLocations.length === 0) {
      return true;
    }
    try {
      const insertResponse = await INSERT_LOCATIONS_MUTATION({
        variables: {
          objects: insertLocations,
        },
      });
      if (!insertResponse.errors) {
        let newLocationId =
          insertResponse.data?.insertUserLocationMaps?.returning[0]
            ?.location_id;
        let requestParams = [
          {
            business_id: locationDetails.businesses[0].business_id,
            location_id: newLocationId,
            job_role_id: locationDetails.roleDetails[0].role_id,
            count: parseInt(values[`count`]),
          },
        ];
        const result = await UPSERT_EMPLOYEE_COUNT_MUTATION({
          variables: {
            objects: requestParams,
          },
        });
        if (!result.errors) {
          const response = await UPDATE_BUSINESS_MUTATION({
            variables: {
              business_id: locationDetails.businesses[0].business_id,
              location_count: locationDetails.businesses[0].location_count + 1,
              business_name: locationDetails.businesses[0].business_name,
              business_type_id: locationDetails.businesses[0].business_type_id,
              owner_id: locationDetails.businesses[0].owner_id,
              referral_code: locationDetails.businesses[0].ref_code,
            },
          });
          if (!response.errors) {
            if (!!upcomingStripePriceId) {
              const isUpgradSuccessful = await UPGRAD_SUBSCRIPTION_MUTATION({
                variables: {
                  price_id: upcomingStripePriceId,
                  locations:
                    subcriptionResult?.data?.update_subscription_on_location
                      ?.returning?.quantity,
                  status: "",
                },
              });
              if (!isUpgradSuccessful.errors) {
                form.resetFields();
                message.success("Location data saved successfully!");
                toggleDrawer();
              } else {
                isUpgradSuccessful.errors.forEach((e) => {
                  return message.error(e.message);
                });
              }
            } else {
              form.resetFields();
              message.success("Location data saved successfully!");
              toggleDrawer();
            }
          }
        }
      } else {
        return false;
      }
    } catch (error) {}
  };

  const handleZipcodeChange = async (e) => {
    if (
      !form.getFieldValue("zipcode") ||
      form.getFieldError("zipcode").length !== 0
    ) {
      return false;
    }
    const zipcode = e.target.value;
    let data = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${zipcode}&sensor=true&key=${process.env.REACT_APP_GOOGLE_MAP_API_KEY}`
    );
    if (data.data.status !== "OK") {
      message.error("Encountered error with zipcode. Please try again later.");
      return false;
    }
    if (data.data.results.length > 0) {
      let latLng =
        data.data.results[0].geometry.location.lat +
        "," +
        data.data.results[0].geometry.location.lng;
      let timeZoneData = await axios.get(
        `https://maps.googleapis.com/maps/api/timezone/json?location=${latLng}&timestamp=${moment().unix()}&key=${
          process.env.REACT_APP_GOOGLE_MAP_API_KEY
        }`
      );

      let addressComponents = data.data.results[0].address_components;
      let addrObj = {};
      addrObj["timezone_id"] = timeZoneData.data.timeZoneId;
      addrObj["timezone_name"] = timeZoneData.data.timeZoneName;
      addressComponents.forEach(({ types, long_name }) => {
        if (types.includes("locality")) {
          addrObj["city"] = long_name;
        }
        if (types.includes("administrative_area_level_2") && !addrObj["city"]) {
          addrObj["city"] = long_name;
        } else if (types.includes("administrative_area_level_1")) {
          addrObj["state"] = long_name;
        } else if (types.includes("country")) {
          addrObj["country"] = long_name;
        }
      });
      form.setFieldsValue(addrObj);
    }
  };

  return (
    <Drawer
      placement="right"
      closable={false}
      width={720}
      visible={visible}
      maskStyle={{ backgroundColor: "#000000CC" }}
      className="newLocation"
    >
      <Row className="headerRow" justify="space-between" align="middle">
        <Col>
          <h5 className="mb-0">Add New Location</h5>
        </Col>
        <Col>
          <Button type="text" icon={<CloseOutlined />} onClick={toggleDrawer} />
        </Col>
      </Row>
      {subscriptionDataLoading ? (
        <Loader />
      ) : (
        <Form form={form} onFinish={confirm}>
          <Row className="mt-4 mx-3" gutter={16}>
            <Col xs={24} sm={24}>
              <Tooltip
                trigger={["focus"]}
                title="Location"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="location_name"
                  rules={[
                    {
                      required: true,
                      message: "Location name is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input name="locationname" placeholder="Location Name" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} xl={8}>
              <Tooltip
                trigger={["focus"]}
                title="Zip Code"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="zipcode"
                  label=""
                  rules={[
                    {
                      required: true,
                      message: "zipcode is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input
                    onBlur={(e) => handleZipcodeChange(e)}
                    name="zipcode"
                    placeholder="Zip Code"
                  />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} xl={16}>
              <Tooltip
                trigger={["focus"]}
                title="Street address"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="street_address"
                  label=""
                  rules={[
                    {
                      required: true,
                      message: "Street Address is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input placeholder="Street Address" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} xl={12}>
              <Tooltip
                trigger={["focus"]}
                title="City"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="city"
                  label=""
                  rules={[
                    {
                      required: true,
                      message: "City is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input disabled placeholder="City" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} xl={12}>
              <Tooltip
                trigger={["focus"]}
                title="State"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="state"
                  label=""
                  rules={[
                    {
                      required: true,
                      message: "State is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input disabled placeholder="State" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} xl={12}>
              <Tooltip
                trigger={["focus"]}
                title="Country"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="country"
                  label=""
                  rules={[
                    {
                      required: true,
                      message: "Country is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <Input disabled placeholder="Country" />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={24} sm={12} xl={12}>
              <Tooltip
                trigger={["focus"]}
                title="Count"
                placement="topLeft"
                overlayClassName="numeric-input"
              >
                <Form.Item
                  name="count"
                  label=""
                  rules={[
                    {
                      pattern: /^(?:\d*)$/,
                      required: true,
                      message: "Employee Count is required",
                      whitespace: true,
                    },
                  ]}
                >
                  <InputNumber
                    placeholder="Employee Count"
                    min={1}
                    max={50}
                    style={{ minWidth: "100%" }}
                  />
                </Form.Item>
              </Tooltip>
            </Col>

            <Col xs={24} md={5}>
              <label
                className="mt-md-2 mb-md-0 mb-2 pl-3 pt-1"
                style={{ fontWeight: "500", color: "#232323" }}
              >
                Working Hours
              </label>
            </Col>
            <Col xs={10} sm={11} md={6}>
              <Tooltip
                trigger={["focus"]}
                title="Start Time"
                placement="topLeft"
              >
                <Form.Item
                  name={`start_time`}
                  rules={[
                    {
                      required: true,
                      message: "Start time is required",
                    },
                  ]}
                >
                  <TimePicker
                    use12Hours
                    allowClear={false}
                    minuteStep={15}
                    showSecond={false}
                    format="h:mm a"
                    suffixIcon={null}
                    placeholder="00:00 AM"
                    className="w-100"
                  />
                </Form.Item>
              </Tooltip>
            </Col>
            <Col xs={4} sm={2} md={1} className="text-center pt-2 pl-0 pr-0">
              <span>to</span>
            </Col>
            <Col xs={10} sm={11} md={6}>
              <Tooltip trigger={["focus"]} title="End Time" placement="topLeft">
                <Form.Item
                  name={`end_time`}
                  rules={[
                    {
                      required: true,
                      message: "End time is required",
                    },
                  ]}
                >
                  <TimePicker
                    use12Hours
                    allowClear={false}
                    minuteStep={15}
                    showSecond={false}
                    format="h:mm a"
                    suffixIcon={null}
                    placeholder="00:00 AM"
                    className="w-100"
                  />
                </Form.Item>
              </Tooltip>
            </Col>
          </Row>
          {/* <Divider className="mx-5" style={{minWidth: 'auto', width: 'auto'}}></Divider>

          <div>
            
            <Row className="headerRowSubscription mt-5" justify="space-between" align="middle">
              <Col>
                <h5>Subscription amount</h5>
                <span>${pay}/Month</span>
              </Col>
              <Col>
                <h5>Subscription Date</h5>
                <span>{moment().format("DD MMM YYYY")}</span>
              </Col>
              <Col>
                <h5>Subscription Billed On</h5>
                <span>{subscriptionType ===
                  "month"
                  ? `${moment().format("Do")} of every month`
                  : `${moment()
                    .add(30, "days")
                    .format("Do MMMM")} of every year`}</span>
              </Col>
            </Row>
          </div> */}
          <div className="mt-3 mb-4 text-center">
            {isUpdatingSubscription ||
            insertLocationLoading ||
            updateBussinessLoading ||
            upsertEmployeeLoading ? (
              <Loader></Loader>
            ) : (
              <Space>
                <Button
                  type="primary"
                  shape="round"
                  size="large"
                  htmlType="submit"
                  style={{ color: "#fff" }}
                  loading={isUpdatingSubscription}
                >
                  <div className="mx-5">Submit</div>
                </Button>{" "}
              </Space>
            )}
          </div>
        </Form>
      )}
    </Drawer>
  );
};
